import React from "react";
import {useState, useEffect} from "react";
import {Controller, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Form, Button, Icon, Input, Message, Label, Dropdown} from "semantic-ui-react";
import Flatpickr from "react-flatpickr";
import "./DartsPlayersCRUD.form.scss";
import toolTip from '../../../img/info-tooltip.png';
import { PlayerAdcType } from "../../../common/enums/playerAdcTypes";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { createDartsPlayer, editDartsPlayer, playersHideForm } from "./DartsPlayersCRUD.slice";
import type { DartsPlayerPayload, DartsPlayerFormValues } from "./DartsPlayersCRUD.types";
import { replaceSpacesWithUnderscore } from "./helpers/replaceSpacesWithUnderscore";
import { schema } from "./DartsPlayersCRUD.schema";
import { Gender } from "../../../common/enums/Gender";

const adcOptions = [
    { key: PlayerAdcType.UK, value: PlayerAdcType.UK, text: 'ADC UK' },
    { key: PlayerAdcType.Europe, value: PlayerAdcType.Europe, text: 'ADC Europe' },
    { key: PlayerAdcType.Oceania, value: PlayerAdcType.Oceania, text: 'ADC Oceania' }
];

const genderOptions = [
    { label: 'Male', value: Gender.Male },
    { label: 'Female', value: Gender.Female },
    { label: 'Other', value: Gender.Other },
];

const DartsPlayersCRUDForm = () => {
    const dispatch = useAppDispatch();

    const { control, formState: { errors }, handleSubmit, getValues, setValue, reset, watch } =
        useForm({
            mode: "all", resolver: yupResolver(schema),
            defaultValues: {
                name: "",
                surname: "",
                nickname: null,
                abbr: null,
                displayname: null,
                hometown: "",
                nationality: "",
                dartsUsed: "",
                careerBest: "",
                adcQualifier: null,
                gender: null
            }
        });

    const { editMode, dartsPlayers, editPlayerId } = useAppSelector(state => state.DartsPlayersCRUD);
    const [genderValue, setGenderValue] = useState("Male");
    const [throwHand, setThrowHand] = useState("Right");
    const [dateOfBirth, setDateOfBirth] = useState<Date | string>(new Date());
    const dayPickerOptions = {
        dateFormat: "Y-m-d",
        locale: {
            firstDayOfWeek: 1,
        },
    };
    const name = watch("name") || "";
    const surname = watch("surname") || "";


    const displayName = replaceSpacesWithUnderscore(name) +
        (surname ? " " + replaceSpacesWithUnderscore(surname) : "");

    useEffect(() => {
        if (displayName !== "undefined undefined") {
            setValue("displayname", displayName.slice(0, 24));
        }
    }, [name, surname, displayName, setValue]);
    useEffect(() => {
        setDefaultFormValues()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editPlayerId, editMode]);


    useEffect(() => {
        const updateCandidate = dartsPlayers.find(
            (player) => player.id === editPlayerId
        );
        if (editMode && updateCandidate) {
            const [initialName, initialSurname] = updateCandidate.displayname.split(" ");
            //setValue("name", initialName);
            setValue("surname", initialSurname || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, setValue]);

    const setDefaultFormValues = () => {
        if (editPlayerId && editMode) {
            const updateCandidate = dartsPlayers.find(
                (player) => player.id === editPlayerId
            );
            for (let key in updateCandidate) {
                // @ts-ignore
                setValue(key, updateCandidate[key]);
            }
            setGenderValue(updateCandidate!.gender);
            setThrowHand(updateCandidate!.throwHand);
            setDateOfBirth(updateCandidate!.dateOfBirth)
        }
    }


    const editPlayer = () => {
        const adcVal = getValues("adcQualifier") as PlayerAdcType | null;
        const updatedPlayer: DartsPlayerPayload = {
            id: editPlayerId!,
            name: getValues("name"),
            surname: getValues("surname"),
            nickname: getValues("nickname")?.trim() ?? '',
            abbr: getValues("abbr") ?? '',
            displayname: getValues("displayname") ?? '',
            gender: genderValue,
            hometown: getValues("hometown"),
            nationality: getValues("nationality"),
            dartsUsed: getValues("dartsUsed"),
            careerBest: getValues("careerBest"),
            throwHand: throwHand,
            dateOfBirth,
            adcQualifier: adcVal == null ? null : Number(adcVal) as PlayerAdcType
        };
        dispatch(editDartsPlayer(updatedPlayer));
        reset();
    };

    const submitPlayer = () => {
        const adcVal = getValues("adcQualifier") as PlayerAdcType | null;
        const newPlayer: DartsPlayerPayload = {
            name: getValues("name"),
            surname: getValues("surname"),
            nickname: getValues("nickname")?.trim() ?? '',
            abbr: getValues("abbr") ?? '',
            gender: genderValue,
            displayname: getValues("displayname") ?? '',
            hometown: getValues("hometown"),
            nationality: getValues("nationality"),
            dartsUsed: getValues("dartsUsed"),
            careerBest: getValues("careerBest"),
            throwHand: throwHand,
            dateOfBirth,
            adcQualifier: adcVal == null ? null : Number(adcVal) as PlayerAdcType
        };
        dispatch(createDartsPlayer(newPlayer));
        reset();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGenderValue(e.target.value)
    }
    const handleChangeThrowHand = (e: React.ChangeEvent<HTMLInputElement>) => {
        setThrowHand(e.target.value);
    }
    return (
        <div className="form-overlay-general">
            <Form className="players-form"
                onSubmit={!editMode
                    ? handleSubmit(submitPlayer)
                    : handleSubmit(editPlayer)
                }>
                <Icon color="red" name="close" size="large" style={{float: "right", cursor: "pointer"}}
                    onClick={() => dispatch(playersHideForm())} />
                <div className={"form-row"}>
                    <Form.Field>
                        <Label className='players-form-label'>Name</Label>
                        <Controller name="name" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="name" error={!!errors.name} placeholder="enter name" {...field} />
                            )}
                        />
                        {errors.name &&
                            <Message negative>{errors.name.message}</Message>
                        }
                    </Form.Field>
                    <Form.Field>
                        <Label className='players-form-label'>Surname</Label>
                        <Controller name="surname" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="surname" error={!!errors.surname} placeholder="enter surname" {...field} />
                            )}
                        />
                        {errors.surname &&
                            <Message negative>{errors.surname.message}</Message>
                        }
                    </Form.Field>
                    <Form.Field>
                        <Label className='players-form-label'>Nickname</Label>
                        <Controller name="nickname" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="nickname" placeholder="enter nickname" {...field} error={!!errors.nickname} />
                            )}
                        />
                        {errors.nickname &&
                            <Message negative>{errors.nickname.message}</Message>
                        }
                    </Form.Field>
                </div>
                <div className={"form-row"}>
                    <Form.Field className={"display-name"}>
                        <div className="players-form-tooltip-wrapper">
                            <Label className='players-form-label'>Display name</Label>
                            <div className="tooltip">
                                <img className={'players-form-tooltip-icon'} src={toolTip} alt={'tooltip-icon'}/>
                                <span>Players first and second names should be separated by a space. For any first name or second name that has multiple words, each word should be separated by an underscore (_). For example: Gian van Veen should be entered as Gian van_Veen, or Jimmy Floyd Hasselbaink should be entered as Jimmy_Floyd Hasselbaink. This will display their name without the underscore, but will correctly separate their first and last names for display purposes.</span>
                            </div>
                        </div>
                        <Controller name="displayname" control={control}
                            render={({ field }) => (
                                <Input id="displayname" error={!!errors.displayname} maxLength="24" placeholder="enter displayName (character limits - 24)" {...field} />
                            )}
                        />
                        {errors.displayname &&
                            <Message negative>{"Value ​​greater than 16 characters Please fill in correctly"}</Message>
                        }
                    </Form.Field>
                    <Form.Field className={"abbr"}>
                        <Label className='players-form-label'>Abbr</Label>
                        <Controller name="abbr" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="abbr" error={!!errors.abbr} maxLength="3" placeholder="enter abbreviation (character limits - 3)" {...field} />
                            )}
                        />
                        {errors.abbr &&
                            <Message negative>{errors.abbr.message}</Message>
                        }
                    </Form.Field>
                </div>
                <div className="radio-input-inner">
                    <Form.Field>
                        <Label className='players-form-label'>Gender</Label>
                        <Controller name="gender" control={control} rules={{ required: 'Please select an option' }}
                            render={({ field }) => (
                                <>
                                    {genderOptions.map((option) => (
                                        <Form.Field className="player-form-input-radio"
                                            key={option.value}
                                            label={option.label}
                                            control="input"
                                            type="radio"
                                            checked={field.value === option.value}
                                            error={!!errors.gender}
                                            {...field}
                                        />
                                    ))}
                                </>
                            )}
                        />
                    </Form.Field>
                </div>
                <div className={"form-row"}>
                    <Form.Field className={"date-of-birth"}>
                        <Label className='players-form-label'>Date of Birth:</Label>
                        <Flatpickr
                            value={dateOfBirth}
                            options={dayPickerOptions}
                            onChange={(date: Date, dayStr: string) => setDateOfBirth(dayStr)}
                        />
                    </Form.Field>
                    <Form.Field>
                        <Label className='players-form-label'>Hometown</Label>
                        <Controller name="hometown" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="hometown" error={!!errors.hometown} maxLength="3" placeholder="enter hometown" {...field} />
                            )}
                        />
                        {errors.hometown && (
                            <Message negative>{errors.hometown.message}</Message>
                        )}
                    </Form.Field>
                    <Form.Field>
                        <Label className='players-form-label'>Nationality</Label>
                        <Controller name="nationality" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="nationality" error={!!errors.nationality} placeholder="enter nationality" {...field} />
                            )}
                        />
                        {errors.nationality && (
                            <Message negative>{errors.nationality.message}</Message>
                        )}
                    </Form.Field>
                </div>
                <div className={"form-row"}>
                    <Form.Field className={"dart-used"}>
                        <Label className='players-form-label'>Darts Used</Label>
                        <Controller name="dartsUsed" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="dartsUsed" error={!!errors.dartsUsed} maxLength="3" placeholder="enter dartsUsed" {...field} />
                            )}
                        />
                        {errors.dartsUsed && (
                            <Message negative>{errors.dartsUsed.message}</Message>
                        )}
                    </Form.Field>
                    <div className={"throw-hand"}>
                        <Label className='players-form-label throw-hand-label'>Throw hand:</Label>
                        <div className="throw-input-inner">
                            <Form.Field>
                                <Label className='players-form-label'>Right</Label>
                                <input
                                    className="player-form-input-radio"
                                    name="Right"
                                    type="radio"
                                    value="Right"
                                    checked={throwHand === "Right"}
                                    //@ts-ignore
                                    onChange={handleChangeThrowHand}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Label className='players-form-label'>Left</Label>
                                <input
                                    className="player-form-input-radio"
                                    name="Left"
                                    type="radio"
                                    value="Left"
                                    checked={throwHand === "Left"}
                                    //@ts-ignore
                                    onChange={handleChangeThrowHand}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Label className='players-form-label'>Both</Label>
                                <input
                                    className="player-form-input-radio"
                                    name="Both"
                                    type="radio"
                                    value="Both"
                                    checked={throwHand === "Both"}
                                    onChange={handleChangeThrowHand}
                                />
                            </Form.Field>
                        </div>
                    </div>
                </div>
                <div className={"form-row"}>
                    <Form.Field className={"career-best"}>
                        <Label className='players-form-label'>Career Best</Label>
                        <Controller name="careerBest" control={control} defaultValue=""
                            render={({ field }) => (
                                <Input id="careerBest" error={!!errors.careerBest} maxLength="3" placeholder="enter career best" {...field} />
                            )}
                        />
                        {errors.careerBest && (
                            <Message negative>{errors.careerBest.message}</Message>
                        )}
                    </Form.Field>
                    <Form.Field className={"player-adc-qualifier"}>
                        <Label className='players-form-label'>ADC Qualifier</Label>
                        <Controller name="adcQualifier" control={control}
                            render={({ field }) => (
                                <Dropdown clearable selection id="adcQualifier" options={adcOptions} placeholder="select ADC Qualifier"
                                    onChange={(_, data) => field.onChange(data.value ?? null)} />
                            )}
                        />
                    </Form.Field>
                </div>
                <div className={"submit-btn"}>
                    <Button type="submit" id="save_player" color="green">
                        {editMode ? "update" : "submit"}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default DartsPlayersCRUDForm;
